<template>
  <div class="checkout-summary-cart-component">
    <div class="checkout-info-collapsed">
      <Accordion>
        <template #header>
          <span class="font-bold text-text2 dark:text-dmText2">{{ t('checkout.summary.your_cart') }}</span>
          <span class="font-bold text-text1 dark:text-dmText1">({{ summary.total_items }})</span>
        </template>
        <div class="checkout-info-items scrollbar">
          <template v-if="summary.products !== null">
            <div v-for="(product, index) in summary.products" :key="index" class="checkout-info-item">
              <div>
                <div
                  class="cart-image"
                  :style="{ backgroundImage: `url('${product.media}')` }"
                  :title="product.name"></div>
              </div>
              <div class="w-full">
                <p class="item-manufacturer">{{ product.brand }}</p>
                <h5 class="item-name">{{ product.name }}</h5>
                <div v-if="product.fields.length" class="item-fields-wrap">
                  <div v-for="(field, fieldIndex) in product.fields" :key="fieldIndex" class="item-fields">
                    <span class="item-field-title">{{ field.title }}:</span>
                    <span class="item-field-value">{{ field.value }}</span>
                  </div>
                </div>
                <div class="item-separator"></div>
                <div class="item-price">
                  <p class="item-price-heading">{{ product.quantity }} {{ t('checkout.summary.qty') }}</p>
                  <div class="item-price-value">
                    {{ toPrice(product.currentPrice * product.quantity) }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </Accordion>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useLang } from '~/composables/useLang'
import { useCheckoutStore } from '~/store/checkout'
import toPrice from '@/utils/toPrice'
import Accordion from '../Forms/Accordion.vue'

const { t } = useLang()
const checkoutStore = useCheckoutStore()
const { isWhitelabel } = useTheme()

const props = defineProps<{
  closed?: boolean
}>()

const summary = computed(() => {
  return {
    products: checkoutStore.products,
    total_items: checkoutStore.totalItems,
  }
})

const collapsibleName = 'mini-cart-1'
const open = ref<Array<string>>(props.closed ? [] : [collapsibleName])
const emit = defineEmits(['open'])

watch(open, (newValue: Array<string>) => {
  emit('open', newValue.includes(collapsibleName))
})

onMounted(() => {
  emit('open', open.value.includes(collapsibleName))
})
</script>

<style lang="postcss">
.checkout-summary-cart-component {
  .checkout-info-items {
    @apply flex flex-col gap-4 max-h-[17.125rem] overflow-x-auto;
    margin-right: -4px;

    .checkout-info-item {
      @apply flex gap-3 border border-solid border-neutral6 dark:border-dmGray1 rounded-sm p-4 dark:bg-dmGray2;
      margin-right: 4px;

      .item-manufacturer {
        @apply flex items-center text-sm font-medium py-1 text-text2 dark:text-dmText2;
      }

      .item-name {
        @apply flex items-center text-base font-normal py-1 text-black dark:text-dmText1 font-secondary;
      }

      .item-fields-wrap {
        @apply flex gap-0 flex-col lg:flex-row flex-wrap;

        .item-fields {
          @apply flex items-center py-1 pr-4;

          .item-field-title {
            @apply text-xs font-normal text-text2 dark:text-dmText2;
          }

          .item-field-value {
            @apply text-sm font-medium pl-[0.3125rem] text-black dark:text-dmText1;
          }
        }
      }

      .item-separator {
        @apply border-t border-solid border-neutral6 dark:border-dmGray4 my-2;
      }

      .item-price {
        @apply flex gap-4 items-center;

        .item-price-heading {
          @apply text-gray0 dark:text-dmText3 text-sm;

          > span {
            @apply text-black dark:text-white;
          }
        }

        .item-price-value {
          @apply text-lg text-neutral1 dark:text-dmText1 font-bold ml-auto;
        }
      }

      .cart-image {
        background-color: white;
      }
    }
  }

  .checkout-info-items::-webkit-scrollbar {
    width: 16px;
  }

  .checkout-info-items::-webkit-scrollbar-thumb {
    border: 4px solid transparent;
    background-clip: padding-box;
  }

  .checkout-info-items::-webkit-scrollbar-track {
    background: transparent;
  }
}
</style>

<style lang="postcss" scoped>
.cart-image {
  @apply flex w-[3.75rem] h-[3.75rem] min-w-[3.75rem] min-h-[3.75rem] bg-contain bg-no-repeat bg-center rounded-[6px] overflow-hidden;
}

.checkout-summary-cart-component {
  .checkout-info-collapsed {
    :deep(.collapsible) {
      .collapsible-header {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 0.25rem;
        padding: 1.25rem 0 1.25rem 0;
        border-radius: 0;
        border: 0;

        &.-active {
          .icon {
            color: theme('colors.brand.primary');
          }
        }

        .collapsible-body {
          border-radius: 0;
          border: 0;

          .content {
            padding: 0 0 1.25rem 0;
          }
        }
      }
    }
  }
}

.alt .checkout-summary-cart-component {
  .checkout-info-item {
    @apply !rounded-sm;
  }
}
</style>

<style lang="postcss" scoped>
.wl .checkout-summary-cart-component {
  .checkout-info-collapsed {
    :deep(.collapsible) {
      .collapsible-item {
        width: 100%;

        .collapsible-header {
          align-items: center;
        }
      }
    }
  }

  .checkout-info-items {
    .checkout-info-item {
      @apply mr-0 border-x-0 !rounded-none;

      .item-manufacturer {
        @apply uppercase font-bold text-text2;
      }
    }
  }

  .checkout-info-items::-webkit-scrollbar {
    width: 4px;
  }

  .checkout-info-items::-webkit-scrollbar-thumb {
    border: 4px solid transparent;
    border-color: theme('colors.neutral3');
    background-clip: padding-box;
  }

  .checkout-info-items::-webkit-scrollbar-track {
    background: theme('colors.neutral7');
  }
}
</style>
